import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button, Column, FullRow, Row, TextField, Typography } from 'seagull';
import { ArrowUpward } from 'seagull/icons';
import { useStoreActions } from "../../../../store/store-context";
import styles from "./HelpMeWriteTextField.module.scss";
import { HelpMeWriteTextFieldTestIds, PresetType } from "./HelpMeWriteTextField.types";
import PresetButton from "./PresetButton";
import { useAnimatedPlaceholder } from "./useAnimatedPlaceholder";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const HelpMeWriteTextField = _ref => {
  let {
    value,
    onChange: _onChange,
    onSubmit,
    onPresetClick,
    isLoading = false,
    disabled = false,
    conversationId = '',
    isUndoLoading = false
  } = _ref;
  const {
    logBI
  } = useStoreActions();
  const isDisabled = disabled || isLoading || isUndoLoading;
  const placeholderState = useAnimatedPlaceholder(isDisabled, isLoading, value);
  const textFieldRef = useRef(null);
  const handlePresetClick = preset => {
    const eventIdMap = {
      [PresetType.Formalize]: 17004,
      [PresetType.Polish]: 17005,
      [PresetType.Shorten]: 17006,
      [PresetType.Elaborate]: 17007
    };
    logBI(eventIdMap[preset]);
    onPresetClick(preset);
  };
  const handleSubmit = () => {
    if (value !== null && value !== void 0 && value.trim()) {
      logBI(17009, {
        text: value.trim()
      });
    }
    _onChange('');
    onSubmit();
  };
  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey && !isDisabled) {
      e.preventDefault();
      handleSubmit();
    }
  };
  const presetButtons = [{
    type: PresetType.Formalize,
    onClick: () => handlePresetClick(PresetType.Formalize)
  }, {
    type: PresetType.Polish,
    onClick: () => handlePresetClick(PresetType.Polish)
  }, {
    type: PresetType.Elaborate,
    onClick: () => handlePresetClick(PresetType.Elaborate)
  }, {
    type: PresetType.Shorten,
    onClick: () => handlePresetClick(PresetType.Shorten)
  }];
  const showPlaceholder = !isDisabled && !isLoading && !value && !conversationId;
  useEffect(() => {
    if (!isDisabled && textFieldRef.current) {
      const timeoutId = setTimeout(() => {
        var _textFieldRef$current;
        const textarea = (_textFieldRef$current = textFieldRef.current) === null || _textFieldRef$current === void 0 ? void 0 : _textFieldRef$current.querySelector('textarea');
        if (textarea) {
          textarea.focus();
        }
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [isDisabled]);
  return /*#__PURE__*/_jsxs(Column, {
    "data-testid": HelpMeWriteTextFieldTestIds.Root(),
    gap: 8,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.textFieldWrapper,
      children: [/*#__PURE__*/_jsxs("div", {
        className: styles.textFieldContainer,
        ref: textFieldRef,
        children: [/*#__PURE__*/_jsx(TextField, {
          collapseHelperText: false,
          multiline: true,
          minRows: 3,
          maxRows: 5,
          value: value,
          onChange: e => _onChange(e.target.value),
          inputProps: {
            onKeyDown: handleKeyDown
          },
          disabled: isDisabled,
          "data-testid": HelpMeWriteTextFieldTestIds.TextField(),
          inputRootClass: styles.inputRoot,
          placeholder: !showPlaceholder ? 'Write here...' : ''
        }), showPlaceholder && /*#__PURE__*/_jsxs(motion.div, {
          className: styles.animatedPlaceholder,
          animate: {
            opacity: placeholderState.opacity
          },
          transition: {
            duration: placeholderState.duration
          },
          children: [placeholderState.displayedText, placeholderState.isTyping && /*#__PURE__*/_jsx("span", {
            className: styles.cursor,
            children: "|"
          })]
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: styles.footer,
        children: /*#__PURE__*/_jsxs(FullRow, {
          justifyContent: "space-between",
          alignItems: "center",
          padding: 12,
          children: [/*#__PURE__*/_jsx(Row, {
            gap: 8,
            children: presetButtons.map(_ref2 => {
              let {
                type,
                onClick
              } = _ref2;
              return /*#__PURE__*/_jsx(PresetButton, {
                onClick: onClick,
                disabled: isDisabled,
                testId: HelpMeWriteTextFieldTestIds[type](),
                text: type
              }, type);
            })
          }), /*#__PURE__*/_jsx(Button, {
            onClick: handleSubmit,
            disabled: isDisabled,
            "data-testid": HelpMeWriteTextFieldTestIds.SubmitButton(),
            prefixIcon: /*#__PURE__*/_jsx(ArrowUpward, {}),
            className: styles.submitButton
          })]
        })
      })]
    }), /*#__PURE__*/_jsx(Typography, {
      className: styles.footerText,
      variant: "paragraph2",
      skin: "secondary",
      "data-testid": HelpMeWriteTextFieldTestIds.Footer(),
      children: "AI suggestions may not always be accurate. Review and tailor as needed."
    })]
  });
};
export default HelpMeWriteTextField;